import { useMemo, useState } from 'react';
import type { SessionMode } from '../types';
import type { UiKey } from '../data/strings';
import { AppHeader, ConfirmDialog, Fraction, Toast } from '../components/common';
import { CATEGORY_BY_ID } from '../data/categories';
import { useT } from '../hooks/useT';
import { computeStats, formatDuration, getEntry, needsPractice, priorityFor } from '../services/progress';
import { useBank } from '../state/BankContext';
import { useProgress } from '../state/ProgressContext';
import { useSettings } from '../state/SettingsContext';

function pct(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value * 100)}%`;
}

/**
 * "Progress Details" — the page for the parent or caregiver.
 * Deliberately separate from the learner's simple progress page.
 */
/** Session modes as they are named to the learner, for the sessions table. */
const MODE_LABEL: Record<SessionMode, UiKey> = {
  daily: 'todayPractice',
  learn: 'learn',
  test: 'practiceTest',
  mistakes: 'myMistakes',
  listen: 'listenAndAnswer',
  category: 'byTopic',
  'signs-test': 'testSigns',
  'signs-learn': 'learnSigns',
};

export function CaregiverScreen() {
  const t = useT();
  const { uiLang } = useSettings();
  const { questions, signs, issues } = useBank();
  const { progress, resetAll } = useProgress();
  const [confirming, setConfirming] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const stats = useMemo(() => computeStats(progress, questions, signs), [progress, questions, signs]);

  const missed = useMemo(
    () =>
      questions
        .map((q) => ({ q, entry: getEntry(progress, 'question', q.id) }))
        .filter((row) => row.entry.wrong > 0)
        .sort((a, b) => b.entry.wrong - a.entry.wrong)
        .slice(0, 12),
    [questions, progress],
  );

  const review = useMemo(
    () => questions.filter((q) => needsPractice(getEntry(progress, 'question', q.id))).length,
    [questions, progress],
  );

  const sessions = progress.sessions.slice(0, 12);

  return (
    <>
      <AppHeader title={t('caregiver')} />
      <main id="main" className="app-main stack">
        <div className="notice notice--info">{t('caregiverNote')}</div>

        <section className="stat-grid">
          <div className="stat">
            <div className="stat__value">{pct(stats.accuracy)}</div>
            <div className="stat__label">{t('accuracy')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{formatDuration(stats.studyTimeMs, uiLang)}</div>
            <div className="stat__label">{t('studyTime')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{stats.learned}</div>
            <div className="stat__label">{t('mastered')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{review}</div>
            <div className="stat__label">{t('needsReview')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">
              <Fraction value={stats.testsPassed} max={stats.testsTaken} />
            </div>
            <div className="stat__label">{t('testsPassed')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">
              <Fraction value={stats.signsLearned} max={stats.totalSigns} />
            </div>
            <div className="stat__label">{t('signsLearned')}</div>
          </div>
        </section>

        <section className="card">
          <h2 className="card__title">{t('byCategory')}</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('topics')}</th>
                  <th>{t('questionsSeen')}</th>
                  <th>{t('learnedLabel')}</th>
                  <th>{t('needsPractice')}</th>
                  <th>{t('accuracy')}</th>
                </tr>
              </thead>
              <tbody>
                {stats.categories.map((c) => {
                  const meta = CATEGORY_BY_ID[c.id];
                  return (
                    <tr key={c.id}>
                      <td>
                        <span aria-hidden="true">{meta.icon} </span>
                        {uiLang === 'ar' ? meta.nameAr : meta.nameEn}
                      </td>
                      <td>
                        <Fraction value={c.seen} max={c.total} />
                      </td>
                      <td>{c.learned}</td>
                      <td>{c.needsPractice}</td>
                      <td>{pct(c.accuracy)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <h2 className="card__title">{t('mostMissed')}</h2>
          {missed.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{t('noData')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('questionOf')}</th>
                    <th>{t('wrongCount')}</th>
                    <th>{t('correctCount')}</th>
                    <th>{t('needsPractice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {missed.map(({ q, entry }) => (
                    <tr key={q.id}>
                      <td>
                        <div>{uiLang === 'ar' ? q.questionAr : q.questionEn}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }} className="ltr">
                          {q.id}
                        </div>
                      </td>
                      <td>{entry.wrong}</td>
                      <td>{entry.correct}</td>
                      <td>{priorityFor(entry)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="card__title">{t('recentSessions')}</h2>
          {sessions.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{t('noData')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('answered')}</th>
                    <th>{t('correctCount')}</th>
                    <th>{t('yourScore')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div>{t(MODE_LABEL[s.mode] ?? 'learn')}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {new Date(s.startedAt).toLocaleString(uiLang === 'ar' ? 'ar' : 'en-US')}
                        </div>
                      </td>
                      <td>{s.answered}</td>
                      <td>{s.correct}</td>
                      <td>
                        <span className="ltr">
                          {s.mode === 'test' && s.total
                            ? `${s.score}/${s.total} ${s.passed ? '✅' : '❌'}`
                            : `${Math.round((s.correct / Math.max(s.answered, 1)) * 100)}%`}
                        </span>
                        {s.mode === 'test' && !s.total ? (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('notFinished')}</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {issues.length > 0 ? (
          <section className="card">
            <h2 className="card__title">{t('bankIssues')}</h2>
            <ul style={{ margin: 0, paddingInlineStart: '1.2rem' }}>
              {issues.map((issue, i) => (
                <li key={`${issue.origin}-${i}`} className="ltr" style={{ fontSize: '0.85rem' }}>
                  {issue.origin}
                  {issue.id ? ` (${issue.id})` : ''}: {issue.message}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="card stack-sm">
          <h2 className="card__title">{t('dataAndPrivacy')}</h2>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('privacyNote')}</p>
          <button type="button" className="btn btn--danger btn--block" onClick={() => setConfirming(true)}>
            <span className="btn__icon" aria-hidden="true">
              🗑
            </span>
            {t('resetProgress')}
          </button>
        </section>
      </main>

      {confirming ? (
        <ConfirmDialog
          message={t('resetConfirm')}
          confirmLabel={t('resetProgress')}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            resetAll();
            setConfirming(false);
            setToast(t('resetDone'));
          }}
        />
      ) : null}

      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
